import { effect, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { WorkflowStatus } from '../../../shared/enums/workflow-status';
import { TRANSLATION_KEYS } from '../../../shared/i18n/translation-keys';
import { ToastService } from '../../../shared/services/toast';
import { WebSocketEventModel } from '../models/workflow-event';
import { WorkflowLoading } from './workflow-loading';

/**
 * Map of completion routes for each workflow status.
 * To update the route for a specific workflow status, modify this map.
 */
const COMPLETION_ROUTES: Partial<Record<WorkflowStatus, (billOfQuantitiesId: number) => string[]>> =
  {
    [WorkflowStatus.ESTRAZIONE_LAVORAZIONI_COMPATIBILI_COMPLETATA]: (billOfQuantitiesId) => [
      '/computo-metrico/create',
      String(billOfQuantitiesId),
      'review',
    ],
    [WorkflowStatus.ESTRAZIONE_LAVORAZIONI_SUGGERITE_COMPLETATA]: (billOfQuantitiesId) => [
      '/computo-metrico/create',
      String(billOfQuantitiesId),
      'suggestions',
    ],
  };

@Injectable({
  providedIn: 'root',
})
export class WorkflowNotifications {
  private readonly _workflowLoadingService = inject(WorkflowLoading);
  private readonly _toastService = inject(ToastService);
  private readonly _router = inject(Router);
  private readonly _translate = inject(TranslateService);

  constructor() {
    /**
     * Effect to listen for workflow run completions and errors, and show toast notifications accordingly.
     * This effect runs whenever the list of workflow runs changes.
     * It checks each run to see if it has completed or encountered an error.
     */
    effect(() => {
      for (const workflowRun of this._workflowLoadingService.runs()) {
        const isCompleted = workflowRun.isCompleted();
        const isError = workflowRun.isError();

        if (!isCompleted && !isError) {
          continue;
        }

        if (isCompleted) {
          this._notifyCompleted(workflowRun.state());
        } else {
          this._notifyError();
        }

        this._workflowLoadingService.remove(workflowRun.id);
      }
    });
  }

  private _notifyCompleted(state: WebSocketEventModel): void {
    const buildRoute = COMPLETION_ROUTES[state.status];

    this._toastService.notifyPersistent(
      this._translate.instant(TRANSLATION_KEYS.TOATS_MESSAGES.WORKFLOW.COMPLETED),
      'success',
      buildRoute
        ? {
            icon: 'open_in_browser',
            label: this._translate.instant(TRANSLATION_KEYS.BUTTONS.DETAIL),
            onClick: () => {
              this._router.navigate(buildRoute(state.billOfQuantitiesId));
            },
          }
        : undefined,
    );
  }

  private _notifyError(): void {
    this._toastService.notifyPersistent(
      this._translate.instant(TRANSLATION_KEYS.GENERAL_ERROR.WORKFLOW_ERROR),
      'error',
    );
  }
}
